/*
 * Lightbox Component
 *
 * This component provides functionality to generate, open, and close a lightbox.
 * It also dispatches custom events to allow developers to hook into the lightbox lifecycle.
 *
 * Custom Events:
 * - 'beforeOpenLightbox': Dispatched before the lightbox is opened.
 *
 *  Example usage:
 *
 *  document.addEventListener( 'beforeOpenLightbox', ( event ) => {
 *     const { lightbox, content } = event.detail;
 *     console.log( 'Lightbox is about to open', lightbox, content );
 *   } );
 *
 * - 'afterCloseLightbox': Dispatched after the lightbox is closed.
 *
 *   Example usage:
 *
 *   document.addEventListener( 'afterCloseLightbox', ( event ) => {
 *     const { lightbox } = event.detail;
 *     console.log( 'Lightbox has been closed', lightbox );
 *   } );
 */

import { themeSettings } from "../../settings";

/**
 * Generate Lightbox
 *
 * @return {void}
 */
const generateLightbox = () => {

	const lightbox = document.querySelector( '.c-lightbox' );

	if ( lightbox === null ) {

		const lightboxEl = document.createElement( 'div' );
		lightboxEl.className = `c-lightbox`;
		lightboxEl.setAttribute( 'role', 'dialog' );
		lightboxEl.setAttribute( 'aria-label', 'Popup' );
		lightboxEl.setAttribute( 'aria-hidden', 'true' );
		lightboxEl.setAttribute( 'aria-modal', 'true' );

		const lightboxContent = document.createElement( 'div' );
		lightboxContent.className = 'c-lightbox__content';

		const closeButton = document.createElement( 'button' );
		closeButton.className = 'c-lightbox__close js-lightbox-close';
		closeButton.setAttribute( 'aria-label', 'Zamknij popup' );

		// Create the img element
		const closeImage = document.createElement( 'img' );
		closeImage.src = `${themeSettings.templateUrl}/img/icons/icon-close-lightbox.svg`;
		closeImage.width = '50';
		closeImage.height = '50';
		closeImage.loading = 'lazy';
		closeImage.setAttribute( 'aria-hidden', 'true' );
		closeImage.alt = '';

		// Append the img to the button
		closeButton.appendChild( closeImage );
		lightboxEl.appendChild( closeButton );

		const overlay = document.createElement( 'div' );
		overlay.className = 'c-lightbox__overlay js-lightbox-close';

		lightboxEl.appendChild( lightboxContent );
		lightboxEl.appendChild( overlay );
		document.body.appendChild( lightboxEl );
		closeLightbox(); // Ensure closeLightbox is called here

	}

}

/**
 * Close Lightbox
 * @return {void}
 */
const closeLightbox = () => {

	const lightbox = document.querySelector( '.c-lightbox' );
	const lightboxContent = lightbox.querySelector( '.c-lightbox__content' );
	const closeButtons = lightbox.querySelectorAll( '.js-lightbox-close' );
	const overlay = lightbox.querySelector( '.c-lightbox__overlay' );
	const lightboxCloseButton = lightbox.querySelector( '.c-lightbox__close' );

	closeButtons.forEach( ( closeButtonEl ) => {

		closeButtonEl.addEventListener( 'click', () => {

			lightbox.classList.remove( 'c-lightbox--open' );
			lightbox.className = 'c-lightbox';
			overlay.className = 'c-lightbox__overlay js-lightbox-close';
			lightboxCloseButton.className = 'c-lightbox__close js-lightbox-close';
			lightboxContent.innerHTML = '';
			document.dispatchEvent( new CustomEvent( 'afterCloseLightbox', { detail: { lightbox } } ) );

			if ( window.__lightboxOpener && typeof window.__lightboxOpener.focus === 'function' ) {

				window.__lightboxOpener.focus();
				window.__lightboxOpener = null;

			}

		} );

	} );

}

/**
 * Open Lightbox with specific content
 *
 * @param {HTMLElement} content
 * @param {object} options
 * @return {void}
 */
const openLightbox = ( content, options = {} ) => {

	let lightbox = document.querySelector( '.c-lightbox' );

	if ( lightbox === null ) {

		generateLightbox( '' );
		lightbox = document.querySelector( '.c-lightbox' );

	}

	const lightboxContent = lightbox.querySelector( '.c-lightbox__content' );
	const overlay = lightbox.querySelector( '.c-lightbox__overlay' );
	const closeButton = lightbox.querySelector( '.c-lightbox__close' );

	lightboxContent.innerHTML = '';
	lightboxContent.appendChild( content.cloneNode( true ) );

	if ( options.overlayClass && overlay ) {

		overlay.classList.add( options.overlayClass );
		if ( options.overlayClass === 'c-lightbox__overlay--adaptive' && closeButton ) {

			closeButton.classList.add( 'c-lightbox__close--adaptive' );

		}

	}

	if ( options.lightboxClass ) {

		lightbox.classList.add( options.lightboxClass );

	}

	if ( options.opener ) {

		window.__lightboxOpener = options.opener;

	}

	document.dispatchEvent( new CustomEvent( 'beforeOpenLightbox', { detail: { lightbox, content } } ) );

	lightbox.classList.add( 'c-lightbox--open' );

}


/**
 * Stores the original `tabindex` values of navigation elements.
 *
 * This variable is generally used to preserve the state of `tabindex` attributes
 * before any modifications are applied. It helps to revert the `tabindex` values
 * back to their original states when needed.
 *
 * @type {Array<number>}
 */
const originalNavTabindexes = [];

/**
 * Disables the focusability of elements outside of a navigation area.
 *
 * The function identifies all focusable elements on the page, except those within
 * specific containers (e.g., elements with the `.c-lightbox` class). It stores the
 * original `tabindex` values of these elements, then sets their `tabindex` attribute
 * to `-1`, effectively preventing them from being focusable via keyboard navigation.
 *
 * This method is typically used to restrict keyboard navigation to a specific area,
 * such as during the opening of modal dialogs or navigational menus.
 *
 * Focusable elements are identified based on a specific query, including links,
 * buttons, inputs, and other elements with a `tabindex` attribute or a `role` attribute
 * of "button". The original tab indexes are preserved in a separate storage for later restoration.
 */
const setTabindexOutsideNav = () => {

	const focusableElements = document.querySelectorAll(
		'a, button, input, select, textarea, [tabindex]:not([tabindex="-1"]), iframe, video, audio, [role="button"]'
	);
	originalNavTabindexes.length = 0;
	focusableElements.forEach( element => {

		if ( element.closest( '.c-lightbox' ) ) {

			return;

		}
		const originalTabindex = element.getAttribute( 'tabindex' );
		originalNavTabindexes.push( { element, tabindex: originalTabindex } );
		element.setAttribute( 'tabindex', '-1' );

	} );

}


/**
 * Restores the original `tabindex` attributes to elements that were modified outside the navigation context.
 *
 * This function iterates over the `originalNavTabindexes` array, which stores the elements and their associated original `tabindex` values.
 * If the original `tabindex` value was `null`, the `tabindex` attribute is removed from the element.
 * Otherwise, the `tabindex` attribute is set back to its original value.
 * Finally, the `originalNavTabindexes` array is cleared to ensure no lingering references remain.
 */
const restoreTabindexOutsideNav = () => {

	originalNavTabindexes.forEach( item => {

		if ( item.tabindex === null ) {

			item.element.removeAttribute( 'tabindex' );

		} else {

			item.element.setAttribute( 'tabindex', item.tabindex );

		}

	} );
	originalNavTabindexes.length = 0;

}


document.addEventListener( 'beforeOpenLightbox', () => {

	setTabindexOutsideNav();

} );
document.addEventListener( 'afterCloseLightbox', () => {

	restoreTabindexOutsideNav();

} );

document.addEventListener( 'keydown', ( e ) => {

	const lightbox = document.querySelector( '.c-lightbox.c-lightbox--open' );
	if ( lightbox && ( e.key === 'Escape' || e.key === 'Esc' ) ) {


		e.preventDefault();
		const closeBtn = lightbox.querySelector( '.js-lightbox-close' );
		if ( closeBtn ) {

			closeBtn.click();

		}

	}

} );

export { openLightbox };
